import WebLayout from "../layout/WebLayout";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import * as authActions from "../store/action/auth";
import { useStates } from "../hooks/useStates";
import { useCities } from "../hooks/useCities";
import { useNavigate } from "react-router-dom";
import { useCodeFetch } from "../hooks/useCodeFetch";

const InputField = ({ name, label, type, required, ...props }) => (
  <div className={"col-lg-6"}>
    <div className="form-group mb-20">
      <label className="mb-5">
        {label?.charAt(0).toUpperCase() + label.slice(1)}{" "}
        {name === "gstNo" || <span className="text-danger">*</span>}
      </label>
      <input
        name={name}
        type={type}
        className="common-input border-gray-100"
        {...props}
        placeholder={label?.charAt(0).toUpperCase() + label.slice(1)}
        required={name === "gstNo" ? false : required}
      />
    </div>
  </div>
);

const SelectField = ({ name, label, options, ...props }) => (
  <div className={"col-lg-6"}>
    <div className="form-group mb-20">
      <label className="mb-5">
        {label?.charAt(0).toUpperCase() + label.slice(1)}{" "}
        {props.required && <span className="text-danger">*</span>}
      </label>
      <select name={name} className="common-input px-16" {...props}>
        <option value="" disabled>
          Select {label}
        </option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken, code } = useSelector((state) => state.auth);
  const { stateList, city } = useSelector((state) => state.menu);
  const [isLoader, setIsLoader] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [formState, setFormState] = useState({
    name: "",
    businessName: "",
    gstNo: "",
    referBy: "",
    email: "",
    state: "",
    district: "",
    pincode: "",
    address: "",
    mobile: "",
    password: "",
    canteen_type: "",
    canteen_code: "",
  });

  useStates();
  useCities(formState.state);
  useCodeFetch(formState.canteen_type);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Mobile number validation (only 10 digits)
    if (name === "mobile") {
      if (!/^\d*$/.test(value)) return; // Allow only numbers
      if (value.length > 10) return; // Limit to 10 digits
    }

    // Pincode validation (only 6 digits)
    if (name === "pincode") {
      if (!/^\d*$/.test(value)) return; // Allow only numbers
      if (value.length > 6) return; // Limit to 6 digits
    }

    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e, action, successMessage) => {
      e.preventDefault();
      setIsLoader(true);
      try {
        await dispatch(action);
        toast.success(successMessage);
        navigate("/");
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoader(false);
      }
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (accessToken) {
      navigate("/");
    }
  }, [accessToken, navigate]);

  const canteenType = [
    {
      value: "ASC",
      name: "ASC",
    },
    {
      value: "ACS",
      name: "ACS",
    },
    {
      value: "APC",
      name: "APC",
    },
    {
      value: "Other",
      name: "Other",
    },
  ];

  return (
    <WebLayout pageName="Checkout">
      <section className="checkout py-20">
        <div className="container container-lg">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-6 d-flex justify-content-center">
              <div
                className="card mb-10"
                style={{ background: "rgb(236, 255, 245)" }}>
                <div className="card-body">
                  <h6 className={"text-center mb-4"}>
                    {showRegistration ? "BUYER REGISTRATION" : "BUYER LOGIN"}
                  </h6>
                  {showRegistration ? (
                    <>
                      <h6 className="mb-5 text-md text-center">
                        केवल IndianMSME.org से खरीदारी के रूप में पंजीकृत
                      </h6>
                      <h6 className="mb-5 text-md text-center">
                        Registered only as a purchase from IndianMSME.org
                      </h6>
                    </>
                  ) : (
                    ""
                  )}

                  {showRegistration ? (
                    // Registration Form
                    <form
                      onSubmit={(e) =>
                        handleSubmit(
                          e,
                          authActions.register({ ...formState }),
                          "Register Successfully"
                        )
                      }>
                      <div className={"row"}>
                        <SelectField
                          name="canteen_type"
                          label="Buyer Type"
                          value={formState.canteen_type}
                          onChange={handleInputChange}
                          options={canteenType}
                          required
                        />
                        {formState.canteen_type != "Other" ? (
                          <SelectField
                            name="canteen_code"
                            label={`${formState.canteen_type} Code`}
                            value={formState.canteen_code}
                            onChange={handleInputChange}
                            options={code}
                            required={formState.canteen_type != "Other"}
                          />
                        ) : (
                          ""
                        )}

                        {[
                          "name",
                          "businessName",
                          "gstNo",
                          "email",
                          "mobile",
                          "password",
                        ].map((field) => (
                          <InputField
                            key={field}
                            name={field}
                            label={field.replace(/([A-Z])/g, " $1")}
                            type={field === "password" ? "password" : "text"}
                            value={formState[field]}
                            onChange={handleInputChange}
                            required
                          />
                        ))}
                        <SelectField
                          name="state"
                          label="State"
                          value={formState.state}
                          onChange={handleInputChange}
                          options={stateList}
                          required
                        />
                        <SelectField
                          name="district"
                          label="District"
                          value={formState.district}
                          onChange={handleInputChange}
                          options={city}
                          required
                        />
                        <InputField
                          name="pincode"
                          label="Pincode"
                          type="text"
                          value={formState.pincode}
                          onChange={handleInputChange}
                          required
                        />
                        <div className="form-group mb-20">
                          <label className="mb-5">
                            Address <span className="text-danger">*</span>
                          </label>
                          <textarea
                            name="address"
                            className="common-input border-gray-100"
                            value={formState.address}
                            onChange={handleInputChange}
                            placeholder={"Address"}></textarea>
                        </div>
                        <button
                          type="submit"
                          className="btn btn-main mt-10 py-18 w-100 rounded-8"
                          disabled={isLoader}>
                          <b>{isLoader ? "Processing..." : "Register"}</b>
                        </button>
                      </div>
                    </form>
                  ) : (
                    // Login Form
                    <form
                      onSubmit={(e) =>
                        handleSubmit(
                          e,
                          authActions.login(
                            formState.mobile,
                            formState.password
                          ),
                          "Login Successfully"
                        )
                      }>
                      <div className={"row"} style={{ marginTop: 20 }}>
                        <InputField
                          name="mobile"
                          label="Mobile Number"
                          type="text"
                          value={formState.mobile}
                          onChange={handleInputChange}
                          required
                          maxLength={10}
                        />
                        <InputField
                          name="password"
                          label="Password"
                          type="password"
                          value={formState.password}
                          onChange={handleInputChange}
                          required
                        />
                        <button
                          type="submit"
                          className="btn btn-main mt-20 py-18 w-100 rounded-8"
                          disabled={isLoader}>
                          <b>{isLoader ? "Processing..." : "Login"}</b>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>

              <div className="text-center mt-3">
                {!showRegistration ? (
                  <p className="text-muted">
                    Don't have an account?{" "}
                    <button
                      type="button"
                      className="text-primary bg-transparent border-0 p-0 text-decoration-underline cursor-pointer"
                      onClick={() => setShowRegistration(true)}>
                      Register Here
                    </button>
                  </p>
                ) : (
                  <p className="text-muted">
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="text-primary bg-transparent border-0 p-0 text-decoration-underline cursor-pointer"
                      onClick={() => setShowRegistration(false)}>
                      Login Here
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}

export default LoginPage;
